import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {InputNumberProps} from 'rsuite/esm/InputNumber/InputNumber';
import {UseFormSetValue} from 'react-hook-form';

import Slider from './Slider';

interface IGeoIntervalSliderProps extends InputNumberProps<string | number> {
    name: string;
    setValue: UseFormSetValue<any>;
    minimalValue: number;
    maxValue?: number;
    defaultValue?: number;
    disabled?: boolean;
}

const MARKER_INSET_START = 2;
const MARKER_INSET_END = 96;

const clampValue = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
};

const valueToPercent = (value: number, min: number, max: number): number => {
    if (max <= min) return 0;
    return ((clampValue(value, min, max) - min) / (max - min)) * 100;
};

const percentToValue = (percent: number, min: number, max: number): number => {
    if (max <= min) return min;
    return min + (clampValue(percent, 0, 100) / 100) * (max - min);
};

const GeoIntervalSlider: React.FC<IGeoIntervalSliderProps> = ({
    name,
    minimalValue,
    maxValue = 5,
    defaultValue,
    setValue,
    disabled = false,
}) => {
    const effectiveMax = Math.max(maxValue, minimalValue);
    const [currentValue, setCurrentValue] = useState(
        clampValue(defaultValue ?? effectiveMax, minimalValue, effectiveMax),
    );

    const progressPct = useMemo(
        () => valueToPercent(currentValue, minimalValue, effectiveMax),
        [currentValue, minimalValue, effectiveMax],
    );
    const fillPct = Math.min(progressPct, 100);

    const markers = useMemo(() => {
        const count = effectiveMax - minimalValue + 1;
        const positions = count <= 1
            ? [MARKER_INSET_START]
            : Array.from(
                {length: count},
                (_, i) => MARKER_INSET_START + (i / (count - 1)) * (MARKER_INSET_END - MARKER_INSET_START),
            );
        return positions.map((position, i) => ({
            key: `${minimalValue + i}`,
            position,
            label: `${minimalValue + i}`,
            overlapped: position <= fillPct,
        }));
    }, [minimalValue, effectiveMax, fillPct]);

    useEffect(() => {
        setValue(name, currentValue, {shouldDirty: true});
    }, [currentValue, name, setValue]);

    useEffect(() => {
        const next = clampValue(defaultValue ?? effectiveMax, minimalValue, effectiveMax);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCurrentValue(next);
        setValue(name, next, {shouldDirty: false});
    }, [defaultValue, minimalValue, effectiveMax, name, setValue]);

    const onOffset = useCallback((offset: number, width: number) => {
        const rawValue = percentToValue((offset / width) * 100, minimalValue, effectiveMax);
        setCurrentValue(clampValue(Math.round(rawValue), minimalValue, effectiveMax));
    }, [minimalValue, effectiveMax]);

    return (
        <Slider
            name={name}
            value={currentValue}
            min={minimalValue}
            max={effectiveMax}
            progressPct={progressPct}
            fillPct={fillPct}
            markers={markers}
            thumbLabel={currentValue}
            disabled={disabled}
            onOffset={onOffset}
        />
    );
};

export default GeoIntervalSlider;
